import React, {useCallback, useMemo} from 'react';
import axios, {AxiosInstance} from 'axios';
import {startNonReactLoading, stopNonReactLoading} from './LoadingContext.tsx';
import {nonReactShowNotification, NotificationType} from './NotificationContext.tsx';
import {createAxiosInstance} from '../api/axiosConfig.ts';
import {
    AcceptOrderRequest,
    AddFileNoteRequest,
    ApiComponentRequest,
    Appraisal,
    AppraiserCoverageAreaResponse,
    AppraiserDashQuote,
    AppraiserInfo,
    AppraiserProfileFile,
    AssignableAppraiser,
    AutoFillResponse,
    AutoReplyRequest,
    CompDetailsRequest,
    CompDetailsResponse,
    CompListRequest,
    CompListResponse,
    CompSearchRequest,
    CompSearchResponse,
    ConditionalAcceptanceRequest,
    convertApiUser,
    DashboardOrder,
    DashboardSearchRequest,
    DevRef, EditGradingProfileRequest,
    EppPartyInformation,
    FileDownload,
    FormImageType,
    FormSubmissionGeneralFileMeta,
    FullCompanyAppraiser,
    GeneralFile,
    GeneratedFileMessage,
    GradingProfile, GradingProfilesResponse,
    ImageSubmission,
    License,
    MarkAsInspectedFormData,
    Me,
    Mls10DataSheetRequest,
    Mls10DataSheetResponse,
    OnBlurRequest,
    OrderDetails,
    OriginalCompAutoFillRequest,
    PaginatedResponse,
    PaymentHistorySearchRequest,
    PaymentHistorySearchResponse,
    PopulationDensityCategory,
    PrintDownload,
    QCDecision,
    QuoteDetails,
    RawMe,
    ResetTokenStatus,
    ReassignOrderRequest,
    RejectOrderRequest,
    ReportForm,
    ReportFormDefinition,
    ReportFormMetaData,
    RespondQuoteFormData,
    ScheduleInspectionFormData,
    SecureLink,
    SecurityUser,
    SELECT_OPTIONS,
    SelectOption,
    SelectOptionArrayValue,
    SendVerificationResponse,
    SimpleSelectOption,
    StaffOrderDetails,
    SubmissionResponse,
    Trigger,
    UpdateStatusRequest
} from "../api/ApiTypes.ts";
import {FileDownloadType} from "../components/FileViewer.tsx";
import {FormValues} from "../components/dynamicforms/DynamicFormTypes.ts";
import {create} from "zustand";
import type {NavigateFunction} from "react-router-dom";
import {useNavigate} from "react-router-dom";
import {useShallow} from "zustand/react/shallow";
import {
    BackendFullValidationRequest,
    BackendFullValidationResponse,
    TriggerRequest
} from "../components/dynamicforms/fields/customvalidator/AggregateValidator.ts";
import {SUBMIT_MODE} from "../components/dynamicforms/dyanimcFormUtil.tsx";

/**
 * Configuration options for customizing API request behavior
 */
interface RequestCustomizations {
    /** Whether to show error notifications (defaults to true) */
    showError?: boolean;
    /** Whether to show loading indicator (defaults to true) */
    showLoading?: boolean;
    /** Custom error message to display on failure */
    errorMessage?: string;
    /** Logout on 401*/
    handle401?: boolean;

    skipReroute403?: boolean;
}

/**
 * Default customization values
 */
const DEFAULT_CUSTOMIZATIONS: RequestCustomizations = {
    showError: true,
    showLoading: true,
    handle401: true,
    skipReroute403: false
};


// Create a React context for our API
let axiosRef: AxiosInstance | null = null;
let activeRequests = 0;
function getAxios(): AxiosInstance {
    if (!axiosRef) {
        console.debug("no axios instance, creating one");
        axiosRef = createAxiosInstance();
    }
    return axiosRef;
}

// ===== API store =====
type ApiState = {
    isUnauthorized: boolean
    setUnauthorized: (v: boolean) => void
    // a navigation function we set from App via <ApiBootstrap/>
    navigate: NavigateFunction
    setNavigate: (fn: NavigateFunction) => void

    get: <T>(url: string, custom?: RequestCustomizations) => Promise<T>
    post: <T>(url: string, data?: any, custom?: RequestCustomizations) => Promise<T>
}

/**
 * Generic request handler that manages loading states and error notifications
 */
const handleRequest = async <T,>(
    requestFn: () => Promise<T>,
    customizations: RequestCustomizations = {},
    get: () => ApiState,
    set: (patch: Partial<ApiState>) => void
): Promise<T> => {
    // Merge with default customizations
    const options = { ...DEFAULT_CUSTOMIZATIONS, ...customizations };

    // Manage loading state
    if (options.showLoading) {
        activeRequests++;
        if (activeRequests === 1) {
            startNonReactLoading();
        }
    }

    try {
        return await requestFn();
    } catch (error) {
        console.log("error in handleRequest", error);
        if (!options.skipReroute403 && axios.isAxiosError(error) && error.response?.status === 403) {
            nonReactShowNotification(NotificationType.ERROR, "You are not authorized to perform this action");
            const { navigate } = get();
            navigate("/forbidden");
            throw error;
        }
        if (options.handle401 && axios.isAxiosError(error) && error.response?.status === 401) {
            console.log("setting isUnauthorized to true due to 401 response");
            set({ isUnauthorized: true });
            nonReactShowNotification(NotificationType.ERROR, 'Session expired. Please log in again.');
            throw error; // Rethrow to propagate the error
        }
        if (options.showError) {
            const errorMessage =
                options.errorMessage ||
                (axios.isAxiosError(error) && error.response?.data?.message) ||
                'An error occurred';
            nonReactShowNotification(NotificationType.ERROR, errorMessage);
        }
        throw error;
    } finally {
        if (options.showLoading) {
            activeRequests = Math.max(0, activeRequests - 1);
            if (activeRequests === 0) {
                stopNonReactLoading();
            }
        }
    }
};

export const useApiStore = create<ApiState>((set, get) => ({
    isUnauthorized: false,
    setUnauthorized: (v: boolean) => set({ isUnauthorized: v }),

    navigate: () => {}, // no-op until bootstrap runs
    setNavigate: (fn) => set({ navigate: fn }),

    async get<T>(url: string, customizations?: RequestCustomizations) {
        return handleRequest<T>(() => getAxios().get<T>(url).then(r => r.data), customizations, get, set)
    },
    async post<T>(url: string, data?: any, customizations?: RequestCustomizations) {
        return handleRequest<T>(() => getAxios().post<T>(url, data).then(r => r.data), customizations, get, set)
    },
}))

/**
 * Hook for authentication-related API calls
 */
export const useAuthApi = () => {
    const { get, post, isUnauthorized, setUnauthorized } = useApiStore(
        useShallow(s => ({
            get: s.get,
            post: s.post,
            isUnauthorized: s.isUnauthorized,
            setUnauthorized: s.setUnauthorized,
        }))
    );

    return useMemo(() => ({
        login: (credentials: {username: string, password: string}): Promise<Me> =>
            post<RawMe>('/auth/login', credentials, {handle401: false, showError: false})
                .then(convertApiUser)
                .then((user) => {
                    setUnauthorized(false);
                    return user;
                }),

        logout: () : Promise<void> =>
            post<void>('/auth/logout'),

        checkAuth: (): Promise<Me> =>
            get<RawMe>('/auth/logged-in', {
                showError: false, // Don't show error for auth checks
                handle401: false // Don't handle 401 for auth checks
            }).then(convertApiUser)
                .then((user) => {
                    console.debug("checkAuth no error, going to setUnauthorized(false)");
                    setUnauthorized(false);
                    return user;
                }),

        sendVerificationCode: (request: {token: string, phoneNumber?: string, emailAddress?: string}) =>
            post<SendVerificationResponse>('/auth/send-code', request, {
                handle401: false
            }),

        verifyVerificationCode: (request: {code: string, token: string}) =>
            post<SendVerificationResponse>('/auth/verify-code', request, {
                handle401: false,
            }),

        changePassword: (request: {oldPassword: string; newPassword: string}) =>
            post<string>('/auth/change-my-password', request),
        //should only be used in the AuthProvider

        //send reset password link; username is the username or the email address
        sendResetPasswordEmail: (request: {username: string;}) =>
            post<string>('/auth/reset-password', request, {
                showError: false,
                handle401: false,
            }),
        //where the emailed link stands and whose it is, for the landing page
        resetTokenStatus: (request: {token: string}) =>
            post<ResetTokenStatus>('/auth/reset-token-status', request, {
                showError: false,
                handle401: false,
            }),
        //actually reset the password, the username comes from the token
        performResetPassword: (request: {token: string, password: string}) =>
            post<string>('/auth/perform-reset-password', request, {
                handle401: false,
            }),
        //from the login page when the password has expired, logs the user in
        changeExpiredPassword: (request: {username: string, currentPassword: string, newPassword: string}): Promise<Me> =>
            post<RawMe>('/auth/change-expired-password', request, {handle401: false})
                .then(convertApiUser)
                .then((user) => {
                    setUnauthorized(false);
                    return user;
                }),

        backdoor: (request: {username: string;}) =>
            post<RawMe>('auth/back-door', request, {
                showError: false,
                handle401: false,
            })
                .then(convertApiUser)
                .then((user) => {
                    setUnauthorized(false);
                    return user;
                }),
        isUnauthorized,
    }), [isUnauthorized, setUnauthorized]);
};

export const usePropertyValuationApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getMls10DataSheet: (request: Mls10DataSheetRequest) =>
                post<Mls10DataSheetResponse>("/property-valuation/mls-10-data-sheet", request),
            getCompSearch: (request: CompSearchRequest) =>
                post<CompSearchResponse>('/property-valuation/comp-search', request),
        }), [get, post]
    );
}

export const useStaffApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getStaffOrderDetails: (orderId: string) =>
                get<StaffOrderDetails>(`/staff/order-details/${orderId}`, {
                    skipReroute403: true,
                    showError: false
                }),
            getDashboard: () =>
                get<Appraisal[]> ('/staff/dashboard'),

        }), [get, post]
    );
}

export const useOrderApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getOrderDetails: (orderId: string) =>
                get<OrderDetails>(`/order-details/${orderId}`, {
                    skipReroute403: true,
                    showError: false
                }),

            addMessage: (request: {orderId: number, message: string}) =>
                post<string>(`/order-details/message`, request),

            markAsInspected: (request: MarkAsInspectedFormData) =>
                post<string>(`/order-details/mark-inspected`, request),

            scheduleInspection: (request: ScheduleInspectionFormData) =>
                post<string>(`/order-details/schedule-inspection`, request),

            rejectWithConditions: (request: ConditionalAcceptanceRequest) =>
                post<string>(`/order-details/conditional-acceptance`, request),

            reject: (request: RejectOrderRequest) =>
                post<string>(`order-details/reject`, request),

            accept: (request: AcceptOrderRequest) =>
                post<string>(`order-details/accept`, request),

            updateStatus: (request: UpdateStatusRequest) =>
                post<string>(`order-details/update-status`, request),

            addFileNote: (request: AddFileNoteRequest) =>
                post<string>(`order-details/file-message`, request),

            autoReplyYes: (request: AutoReplyRequest) =>
                post<string>(`order-details/auto-reply-yes`, request),

            autoReplyNo: (request: AutoReplyRequest) =>
                post<string>(`order-details/auto-reply-no`, request),

            wasInvited: (orderId: string) =>
                get<boolean>(`/order-details/${orderId}/was-invited`),
        }), [get, post]
    );
}

export const useDashApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getDash: (searchParams: DashboardSearchRequest): Promise<PaginatedResponse<DashboardOrder>> =>
                post<PaginatedResponse<DashboardOrder>>('/dashboard', searchParams)
        }), [get, post]
    );
};
export const useUserApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getSecurityUser: (id: number) =>
                get<SecurityUser>(`/user/${id}`),

        }), [get, post]
    );
}


/**
 * Hook for appraiser-related API calls
 */
export const useAppraiserApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getMyAppraiserInfo: () =>
                get<AppraiserInfo>(`/appraiser/me`),
            getAppraiserInfo: (appraiserId: number) =>
                get<AppraiserInfo>(`/appraiser/${appraiserId}`),

            getCoverageAreas: (appraiserId: number) =>
                get<AppraiserCoverageAreaResponse>(`/appraiser/${appraiserId}/coverage-areas`),

            addCoverageArea: (add: {appraiserId: number; county: string; state: string}) =>
                post<AppraiserCoverageAreaResponse>('/appraiser/coverage-areas/add', add),

            deleteCoverageArea: (deletionRequest: {appraiserId: number; coverageAreaId: number; county: string; state: string}) =>
                post<AppraiserCoverageAreaResponse>('/appraiser/coverage-areas/delete', deletionRequest),

            updateNoTrainee: (update: { appraiserId: number; hasTrainee: boolean }) =>
                post<AppraiserInfo>('/appraiser/update-trainee-info', update),

            updateEandO: (formData: FormData) =>
                post<AppraiserInfo>('/appraiser/update-e-and-o', formData),

            updateOOO: (update: { appraiserId: number; startDate: string; endDate: string }) =>
                post<AppraiserInfo>('/appraiser/update-ooo', update),

            getLicenseInfo: (appraiserId: number) =>
                get<License[]>(`/appraiser/${appraiserId}/license-info`),

            getMySecureLinks: () =>
                get<SecureLink[]>(`/appraiser/secure-links`),
            getPartyAccessUrl: (appraiserId: number) =>
                get<string>(`/appraiser/${appraiserId}/epp-party-access-url`),

            getOnboardingToken: (appraiserId: number) =>
                get<string>(`/appraiser/${appraiserId}/onboard-token`),

            getEppPartyInformation: (appraiserId: number) =>
                get<EppPartyInformation>(`/appraiser/${appraiserId}/epp-party-information`),

            getPaymentHistory: (searchParams: PaymentHistorySearchRequest, appraiserId: number)=>
                post<PaymentHistorySearchResponse[]>(`/appraiser/${appraiserId}/payment-history`, searchParams)
        }), [get, post]
    );
};

/**
 * Hook for constants and lookup data API calls
 */
export const useConstantsApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getStates: () =>
                get<SelectOption[]>(`/constants/${SELECT_OPTIONS.STATES}`),

            getCountiesForState: (stateCode: string) =>
                get<SelectOption[]>(`/constants/counties/${stateCode}`),

            getCellPhoneCarriers: () =>
                get<SelectOption[]>(`/constants/${SELECT_OPTIONS.CELL_PHONE_CARRIERS}`),

            getSearchStatuses: () =>
                get<SelectOptionArrayValue[]>(`/constants/search-statuses`),

            getAppraiserAvailableFileTypes: () =>
                get<SelectOption[]>(`/constants/appraiser-file-types`),

            getOrderAvailableFileTypes: (forOrder: number) =>
                get<SelectOption[]>(`/constants/order-file-types/${forOrder}`),

            getAppraiserStatuses: () =>
                get<SelectOption[]>(`/constants/appraiser-statuses`),

            getStatusesForUpdateStatus: () =>
                get<SelectOption[]>(`/constants/status-message-options`),

            getMobileOperatingSystemTypes: () =>
                get<SelectOption[]>(`/constants/mobile-operating-system-types`),

            getReportTypes: () =>
                get<SelectOption[]>(`/constants/report-types`),

            getAmcTechFee: () =>
                get<number>(`/constants/amc-tech-fee`),
        }), [get, post]
    );
};


/**
 * Hook for appraiser admin API operations
 */
export const useAppraiserAdminApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )


    return useMemo(() => ({
            getAssignableAppraisers: (orderId: number) =>
                get<AssignableAppraiser[]>(`/admin-appraiser/order/${orderId}/assignable`),

            reAssignOrder: (request:ReassignOrderRequest) =>
                post<void>(`/admin-appraiser/assign-order`, request),

            getCompanyAppraisers: () =>
                get<FullCompanyAppraiser[]>(`/admin-appraiser/company-appraisers`),
        }), [get, post]
    );
}

/**
 * Hook for file-related API operations
 */
export const useFileApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    const downloadAppraiserProfileFile = useCallback((fileId: number) =>
        get<FileDownload>(`/file/download/appraiserprofilefile/${fileId}`), [get, post]
    );

    const downloadAppraisalFile = useCallback((fileId: number) =>
        get<FileDownload>(`/file/download/appraisalfile/${fileId}`), [get, post]
    );

    const downloadGeneralFile = useCallback((fileId: number) =>
        get<FileDownload>(`/file/download/generalfile/${fileId}`), [get, post]
    );



    return useMemo(() => ({
        getMyEoFile: (appraiserId: number) =>
            get<AppraiserProfileFile>(`/file/my-e-and-o/${appraiserId}`),

        downloadFile(fileId: number, fileIs: FileDownloadType) {
            if (fileIs === FileDownloadType.APPRAISAL_FILE) {
                return downloadAppraisalFile(fileId);
            }
            else if (fileIs === FileDownloadType.GENERAL_FILE) {
                return downloadGeneralFile(fileId);
            }
            else {
                return downloadAppraiserProfileFile(fileId);
            }
        },

        printOrder: (orderId: number) =>
            post<PrintDownload>(`/file/print-order`, {orderId}),

        getAppraiserDocumentFiles: (appraiserId: number) =>
            get<AppraiserProfileFile[]>(`/file/appraiser-documents/${appraiserId}`),

        uploadFiles: (formData: FormData) =>
            post<number[]>(`/file/upload`, formData),

        uploadFormSubmissionFile: (request: {file: File, fileMeta?: FormSubmissionGeneralFileMeta, formMeta: ReportFormMetaData}) => {
            const formData = new FormData();
            formData.append("file", request.file);
            formData.append("formSubmissionId", request.formMeta.submissionId.toString());
            if (request.fileMeta) {
                if (request.fileMeta.fileType) {
                    formData.append("formSubmissionFileType", request.fileMeta.fileType);
                }
                if (request.fileMeta.fieldId) {
                    formData.append("fieldKey", request.fileMeta.fieldId);
                }
            }
            return post<GeneralFile>(`/file/upload/generalfile`, formData);
        },
        uploadFormImage: (request:
                          { file: File,
                              formMeta: ReportFormMetaData,
                              imageType: FormImageType,
                              fieldKey: string,
                              externalImageUrl: string | undefined}) => {
            const formData = new FormData();
            formData.append("file", request.file);
            formData.append("formSubmissionId", request.formMeta.submissionId.toString());
            formData.append("imageType", request.imageType);
            formData.append("fieldKey", request.fieldKey);
            return post<ImageSubmission>(`/file/upload/form-image`, formData);
        },

        uploadFormImageFromUrl: (request: {
            formSubmissionId: number,
            imageType: FormImageType,
            fieldKey: string,
            externalImageUrl: string
        }) => {
            return post<ImageSubmission>(`/file/upload/form-image-from-url`, request);
        },

        updateFormImage: (request: {formSubmissionId: number, generalFileId: number, imageType: FormImageType}) => {
            return post<ImageSubmission>(`/file/update-form-image`, request);
        },

        updateFormImageMore: (request: {formSubmissionId: number, image: ImageSubmission}) => {
            return post<ImageSubmission>(`/file/update-form-image-more`, request);
        },

        deleteFormSubmissionFile: (fileId: number) =>
            post<void>(`/file/delete/generalfile/${fileId}`),

        getScopeOfWork: (orderId: number) =>
            get<FileDownload>(`/file/download/scope-of-work/${orderId}`)
    }), [get, post, downloadAppraisalFile, downloadAppraiserProfileFile, downloadGeneralFile]);
};

export const useQuotesApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getQuotes: () =>
                get<AppraiserDashQuote[]>(`/quotes/dash`),

            getQuoteDetails: (quoteId: number) =>
                get<QuoteDetails>(`/quotes/details/${quoteId}`),

            acceptQuote: (accept: RespondQuoteFormData) =>
                post<void>(`/quotes/accept`, accept),

            rejectQuote: (reject: {quoteId: number, rejectReason: string}) =>
                post<void>(`/quotes/reject`, reject),

        }), [get, post]
    );
}

export const useAdminFormsApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({

            getAllReportDefinitions: () =>
                get<ReportFormDefinition[]>(`/report-form-admin/all`),

            createReportFormDefinition: (create: {reportTypeId: number, schema: any}) =>
                post<ReportFormDefinition>(`/report-form-admin/create`, create),

            editReportFormDefinition: (create: {reportTypeId: number, newSchema: any, currentFormId: number}) =>
                post<ReportFormDefinition>(`/report-form-admin/edit`, create),

            getExampleOrdersForFormDefinition: (reportTypeId: number) =>
                get<Appraisal[]>(`/report-form-admin/example-by-report/${reportTypeId}`),

            testDeliverableFor: (param: { productOrderId: number; schema: any, useStoredSchema: boolean }) =>
                post<GeneratedFileMessage>(`/report-form-admin/test-deliverable`, param),
            getExampleMetaData: (request:  {exampleOrderId: number, effectiveDate: string | null, populationDensity: PopulationDensityCategory }) =>
                post<ReportFormMetaData>(`/report-form-admin/example-meta-data`, request),

        }), [get, post]
    );
}

export const useFormApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
            getSimpleSelectOptions: (type: string) =>
                get<SimpleSelectOption[]>(`/form-options/${type}`),

            getReportForm: (orderId: number, mode: SUBMIT_MODE) =>
                get<ReportForm>(`/report-form/${mode.toLowerCase()}/product-order/${orderId}`),
            submitFormQC: (
                formDefinitionId: number,
                orderId: number,
                values: FormValues,
                submitMode: SUBMIT_MODE
            ) => {
                return post<SubmissionResponse>(`/report-form/submit-form`, {
                    formDefinitionId: formDefinitionId,
                    orderId: orderId,
                    values: values,
                    submitMode: submitMode
                })
            },
            submitForm: (
                formDefinitionId: number,
                orderId: number,
                values: FormValues,
                submitMode: SUBMIT_MODE,
                qcDecision: QCDecision | undefined,
                notes: string | undefined

            ) => {
                return post<SubmissionResponse>(`/report-form/submit-form`, {
                    formDefinitionId: formDefinitionId,
                    orderId: orderId,
                    values: values,
                    submitMode: submitMode,
                    qcDecision: qcDecision,
                    notes: notes
                })
            },

            saveProgress: (
                formDefinitionId: number,
                submissionId: number,
                orderId: number,
                values: FormValues,
            ) => {
                return post<ReportForm>(`/report-form/save-progress`, {formDefinitionId: formDefinitionId, submissionId: submissionId, orderId: orderId, values:values})
            },
            callFromApiComponent: (request: ApiComponentRequest) => {
                return post<AutoFillResponse>(`/report-form/call-from-api-component`, request, {
                    showError: false
                })
            },
            onBlurCall: (request: OnBlurRequest) => {
                return post<AutoFillResponse>(`/report-form/on-blur-call`, request, {
                    showError: false
                })
            },
            originalCompAutoFillCall: (request: OriginalCompAutoFillRequest) => {
                return post<AutoFillResponse>(`/report-form/original-comp-autofill`, request, {
                    showError: false
                })
            },
            getCustomTriggers: (request: TriggerRequest) => {
                return post<Trigger[]>(`/report-form/custom-triggers`, request, {
                    showError: false
                })
            },
            validateFullForm: (request: BackendFullValidationRequest) => {
                return post<BackendFullValidationResponse>(`/report-form/validate-all-fields`, request, {
                    showError: false
                })
            },
            getCompDetails: (request: CompDetailsRequest) =>
                post<CompDetailsResponse>('/report-form/comp-details', request),
            getCompList: (request: CompListRequest) =>
                post<CompListResponse>('/report-form/comp-list', request),
        }), [get, post]
    );
}

export const useDevApi = () => {
    const { get, post } = useApiStore(
        useShallow(s => ({ get: s.get, post: s.post }))
    )

    return useMemo(() => ({
        getDevRefs: () =>
            get<DevRef[]>(`/dev/dev-refs`),

        createDevRef: (devRef: {name: string, url: string}) =>
            post<DevRef>(`/dev/dev-refs/create`, devRef),

        editDevRef: (devRef: {name: string, url: string, id: number}) =>
            post<DevRef>(`/dev/dev-refs/update`, devRef),
        getCaptures: (poId: number) => {
            return get<GeneralFile[]>(`/dev/ca-captures/${poId}`)
        }
    }), [get, post]);
}

export const useGradingProfileApi = () => {
    const {get, post} = useApiStore(
        useShallow(s => ({get: s.get, post: s.post}))
    )

    return useMemo(() => ({

            getAllGradingProfiles: () =>
                get<GradingProfilesResponse>(`/gradingprofile/getallgradingprofiles`),

            editGradingProfile: (request: EditGradingProfileRequest) =>
                 post<GradingProfile>(`/gradingprofile/editgradingprofile`, request)

        }), [get, post]
    );
}

export function ApiBootstrap() {
    const nav = useNavigate()
    const setNavigate = useApiStore(s => s.setNavigate)
    React.useEffect(() => { setNavigate(nav) }, [nav, setNavigate])
    return null
}
